import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import { Link } from 'react-router-dom';
import { getAttendenceData } from '../../services/dataService';
import { tempLateEmp } from '../../atom/atom';
import { IconArrowBack } from '@tabler/icons-react';
import { getSecureItem } from '../../services/SecureStorage';

// ------------------ Late Time Utility Functions ------------------

const getTimeInMinutes = (dateString) => {
  if (!dateString) return 0;
  const date = new Date(dateString);
  return date.getUTCHours() * 60 + date.getUTCMinutes();
};

const isLateArrival = (row) => {
  if (!row.PUNCHIN || !row.StartTime || !row.RECORDDATE) return false;
  const gracePeriod = parseInt(row.GracePeriod) || 0;
  const punchInMinutes = getTimeInMinutes(row.PUNCHIN);
  const startTimeMinutes = getTimeInMinutes(row.StartTime);
  return punchInMinutes > startTimeMinutes + gracePeriod;
};

const calculateLateTime = (row) => {
  if (!row.PUNCHIN || !row.StartTime) return "-";
  const gracePeriod = parseInt(row.GracePeriod) || 0;
  const punchInMinutes = getTimeInMinutes(row.PUNCHIN);
  const startTimeMinutes = getTimeInMinutes(row.StartTime);
  const allowedStartMinutes = startTimeMinutes + gracePeriod;
  if (punchInMinutes <= allowedStartMinutes) return "-";
  const lateMinutes = punchInMinutes - allowedStartMinutes;
  const hours = Math.floor(lateMinutes / 60);
  const minutes = lateMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

// -----------------------------------------------------------------

function LateEmp() {
  const isAdmin = getSecureItem("IsAdmin");
  const departmentIds = getSecureItem("DepartmentId") || "";
  const DepartmentId = departmentIds.split(",")[0] || null;
  const UID = getSecureItem("userId");

  const [tempLate, setTempLate] = useState([]);
  const [sortedData, setSortedData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedProfitCenter, setSelectedProfitCenter] = useState("all");
  const [loading, setLoading] = useState(true);

  // ------------------ Fetch Attendance Data ------------------
  const getAttendence = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const payload = {
        UserId: UID,
        Date: today.toISOString().split('T')[0],
      };

      const response = await getAttendenceData(payload);
      const filteredData = isAdmin == 1
        ? response?.data?.data || []
        : response?.data?.data?.filter(el => el?.DEPARTMENTID == DepartmentId) || [];

      // Use the same late arrival logic
      const Late = filteredData?.filter(employee => isLateArrival(employee));

      setTempLate(Late);
      console.log(Late, 'Late Employees');
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAttendence();
  }, []);

  // ------------------ Date Display ------------------
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  // ------------------ Grouping and Sorting ------------------
  const groupedAndSortedData = useMemo(() => {
    const filteredEmployees = tempLate.filter(emp => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        emp.EmployeeName?.toLowerCase().includes(searchLower) ||
        emp.EmpCode?.toLowerCase().includes(searchLower)
      );

      const matchesDepartment = selectedDepartment === "all" ||
        emp.DepartmentName === selectedDepartment;

      const matchesProfitCenter = selectedProfitCenter === "all" ||
        emp.ProfitcenterName === selectedProfitCenter;

      return matchesSearch && matchesDepartment && matchesProfitCenter;
    });

    const groupedData = filteredEmployees.reduce((acc, emp) => {
      const dept = emp.DepartmentName || 'Others';
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push({
        ...emp,
        LateTime: calculateLateTime(emp),
      });
      return acc;
    }, {});

    Object.keys(groupedData).forEach(dept => {
      groupedData[dept].sort((a, b) => {
        if (a.LateTime === '-') return 1;
        if (b.LateTime === '-') return -1;
        return a.LateTime.localeCompare(b.LateTime);
      });
    });

    return groupedData;
  }, [tempLate, searchTerm, selectedDepartment, selectedProfitCenter]);

  useEffect(() => {
    setSortedData(groupedAndSortedData);
  }, [groupedAndSortedData]);

  // ------------------ Filters Data ------------------
  const departments = useMemo(() => {
    const depts = [...new Set(tempLate.map(emp => emp.DepartmentName || 'Others'))];
    return depts.sort();
  }, [tempLate]);

  const profitCenters = useMemo(() => {
    const centers = [...new Set(tempLate.map(emp => emp.ProfitcenterName || 'Others'))];
    return centers.sort();
  }, [tempLate]);

  const flattenedData = useMemo(() => {
    const allEmployees = [];
    Object.keys(sortedData).forEach(dept => {
      allEmployees.push(...sortedData[dept].map(emp => ({
        ...emp,
        DepartmentName: dept
      })));
    });
    return allEmployees;
  }, [sortedData]);

  // ------------------ UI Helpers ------------------
  const totalLateEmployees = flattenedData.length;
  const totalDepartments = Object.keys(sortedData).length;

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const time = new Date(timeString);
    return time.toISOString().substr(11, 5);
  };

  // ------------------ JSX ------------------
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Late Employees Report</h1>
            {isAdmin == 0 && flattenedData?.length > 0 && (
              <p className="text-2xl text-gray-800">
                Department: {[...new Set(flattenedData.map((el) => el.DepartmentName))].join(", ")}
              </p>
            )}
            <p className="text-gray-600 text-sm">{currentDate}</p>
          </div>

          <div className="flex gap-4">
            <div className="bg-red-50 rounded-lg p-3 text-center min-w-[100px]">
              <div className="text-2xl font-bold text-red-600">{totalLateEmployees}</div>
              <div className="text-xs text-red-600 font-medium">Late Today</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center min-w-[100px]">
              <div className="text-2xl font-bold text-blue-600">{totalDepartments}</div>
              <div className="text-xs text-blue-600 font-medium">Departments</div>
            </div>

            <Link to={'/'}>
              <button className="bg-gray-50 hover:bg-gray-100 rounded-lg p-3 text-center min-w-[100px] transition-colors cursor-pointer">
                <div className="text-2xl font-bold text-gray-600 flex justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
                <div className="text-xs text-gray-600 font-medium">Go Back</div>
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name or employee code..."
                className="w-full pl-12 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="absolute left-4 top-3 h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {isAdmin == 1 &&
            <div className="md:w-64">
              <select
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>}

          <div className="md:w-64">
            <select
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white"
              value={selectedProfitCenter}
              onChange={(e) => setSelectedProfitCenter(e.target.value)}
            >
              <option value="all">All Profit Centers</option>
              {profitCenters.map(center => (
                <option key={center} value={center}>{center}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {flattenedData.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Late Employees Found</h3>
            <p className="text-gray-500">
              {searchTerm || selectedDepartment !== "all" || selectedProfitCenter !== "all"
                ? "Try adjusting your search or filter criteria."
                : "Great! No employees are late today."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider w-16">SN</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Employee Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Employee Code</th>
                  {isAdmin == 1 && (
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Department</th>
                  )}
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Profit Center</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Shift Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Start Time</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">End Time</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Punch IN</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Punch OUT</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Grace Period</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-900 uppercase tracking-wider">Late Time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {flattenedData.map((emp, idx) => (
                  <tr key={emp.ID} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{idx + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{emp.EmployeeName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">{emp.EmpCode}</td>
                    {isAdmin == 1 && (
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-blue-800">{emp.DepartmentName}</td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-blue-800">{emp.ProfitcenterName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-blue-800">{emp.ShiftName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-blue-800">{emp.StartTime ? formatTime(emp.StartTime) : ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-blue-800">{emp.EndTime ? formatTime(emp.EndTime) : ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-blue-800">{emp.PUNCHIN ? formatTime(emp.PUNCHIN) : ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-blue-800">{emp.PUNCHOUT ? formatTime(emp.PUNCHOUT) : ''}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-blue-800">{emp.GracePeriod}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {emp.LateTime !== '-' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {emp.LateTime}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer */}
      {flattenedData.length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {flattenedData.length} late employee{flattenedData.length !== 1 ? 's' : ''}
              {selectedDepartment !== "all" ? ` in ${selectedDepartment}` : ''}
              {selectedProfitCenter !== "all" ? ` in ${selectedProfitCenter}` : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default LateEmp;
